// src/components/ui/StepUploadBubble.tsx
import React, { useEffect, useRef, useState } from "react";
import { N8N_FILE_UPLOAD_URL } from "../../config/n8n";
import { Camera, FileIcon, Paperclip } from "lucide-react";

export type ArtifactConfig = {
  id: string;
  stepId: string;
  label: string;
  hint?: string;
  type: "photo" | "video" | "audio" | "file";
  accept?: string[];
  multiple?: boolean;
  required?: boolean;
  maxSizeMB?: number;
};

interface Props {
  artifact: ArtifactConfig;
  sessionId: string;
  accessCode?: string;
  /** Explicit database routing; defaults to Airtable for regression safety. */
  db?: "airtable" | "supabase";
  /** Parent uses this to enable its Finish button. */
  onUploaded?: (payload: {
    storagePath?: string;
    publicUrl?: string;
    assetId?: string;
    filename: string;
    mime: string;
    size: number;
    width?: number;
    height?: number;
    sortOrder?: number;
  }) => void;
}

export interface StepUploadMetadata {
  storagePath?: string;
  publicUrl?: string;
  assetId?: string;
  filename: string;
  mime: string;
  size: number;
  width?: number;
  height?: number;
  sortOrder?: number;
}

export function buildStepUploadFormData({
  file,
  sessionId,
  stepId,
  artifactId,
  accessCode = "",
  db = "airtable",
}: {
  file: File;
  sessionId: string;
  stepId: string;
  artifactId: string;
  accessCode?: string;
  db?: "airtable" | "supabase";
}): FormData {
  const form = new FormData();
  form.append("file", file);
  form.append("sessionId", sessionId);
  form.append("stepId", stepId);
  form.append("artifactId", artifactId);
  form.append("accessCode", accessCode);
  form.append("db", db);
  return form;
}

export function normalizeStepUploadMetadata(
  value: unknown,
  file: File,
): StepUploadMetadata {
  const json =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  return {
    ...(typeof json.storagePath === "string"
      ? { storagePath: json.storagePath }
      : {}),
    ...(typeof json.publicUrl === "string"
      ? { publicUrl: json.publicUrl }
      : {}),
    ...(typeof (json.assetId ?? json.id) === "string"
      ? { assetId: String(json.assetId ?? json.id) }
      : {}),
    filename: typeof json.filename === "string" ? json.filename : file.name,
    mime:
      typeof (json.mime ?? json.mimeType) === "string"
        ? String(json.mime ?? json.mimeType)
        : file.type,
    size:
      typeof (json.size ?? json.sizeBytes) === "number"
        ? Number(json.size ?? json.sizeBytes)
        : file.size,
    ...(typeof json.width === "number" ? { width: json.width } : {}),
    ...(typeof json.height === "number" ? { height: json.height } : {}),
    ...(typeof json.sortOrder === "number"
      ? { sortOrder: json.sortOrder }
      : {}),
  };
}

/**
 * NOTE:
 * We intentionally DO NOT notify Main Chat from here.
 * After a successful upload we only show "Uploaded ✓" and invoke onUploaded(),
 * so the parent can enable a Finish button and send the short callback
 * ("Upload complete: <label>") when the user taps Finish.
 */
const USE_FILE_EVENT = false;

const StepUploadBubble: React.FC<Props> = ({
  artifact,
  sessionId,
  accessCode,
  db = "airtable",
  onUploaded,
}) => {
  // ✅ Do NOT return here; just compute support flag
  const isSupported =
    !!artifact &&
    (["photo", "image", "file", "video"] as const).includes(
      artifact.type as any,
    );

  // ✅ Hooks must be unconditional (always called in same order)
  const inputRef = useRef<HTMLInputElement | null>(null); // desktop + non-photo fallback
  const cameraInputRef = useRef<HTMLInputElement | null>(null); // mobile "Take photo"
  const libraryInputRef = useRef<HTMLInputElement | null>(null); // mobile "Choose from photos"
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const uploadedOnceRef = useRef(false); // de-dupe onUploaded per successful upload

  // ✅ Only latest selection should trigger upload (prevents stale/double uploads)
  const pendingAutoUploadRef = useRef<File | null>(null);

  // ✅ Track preview URL via ref so we can revoke on unmount without tying effect to previewUrl
  const previewUrlRef = useRef<string | null>(null);

  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [uploadedMeta, setUploadedMeta] = useState<StepUploadMetadata | null>(
    null,
  );

  // ✅ Abort upload helper
  const cancelUpload = () => {
    if (xhrRef.current) {
      try {
        xhrRef.current.abort();
      } catch {}
      xhrRef.current = null;
    }
  };

  // ✅ Revoke preview helper
  const revokePreview = () => {
    const u = previewUrlRef.current;
    if (u) {
      try {
        URL.revokeObjectURL(u);
      } catch {}
      previewUrlRef.current = null;
    }
  };

  // ✅ Cleanup ONLY on unmount (do NOT depend on previewUrl!)
  useEffect(() => {
    return () => {
      cancelUpload();
      revokePreview();
      pendingAutoUploadRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset per-artifact: if a different step/artifact renders this bubble,
  // clear the de-dupe and local state.
  useEffect(() => {
    uploadedOnceRef.current = false;
    pendingAutoUploadRef.current = null;

    // Abort any in-flight upload when artifact changes
    cancelUpload();

    setError(null);
    setFile(null);

    // revoke previous preview
    revokePreview();
    setPreviewUrl(null);

    setProgress(0);
    setUploading(false);
    setUploadedMeta(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifact.id]);

  // ⛔️ Only now is it safe to exit early
  if (!isSupported) return null;

  // Vars that depend on artifact go after the safe early return
  const accept =
    db === "supabase"
      ? "image/*"
      : artifact.accept?.join(",") ||
        (artifact.type === "photo" || (artifact as any).type === "image"
          ? "image/*"
          : undefined);
  const maxSize = (artifact.maxSizeMB ?? 10) * 1024 * 1024;

  const isPhoto =
    artifact.type === "photo" || (artifact as any).type === "image";

  const isMobile =
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Only show 2-button UI for photo/image on mobile.
  const showMobilePhotoButtons = isPhoto && isMobile;

  // Desktop + non-photo picker
  const openPicker = () => inputRef.current?.click();
  // Mobile photo actions
  const openCamera = () => cameraInputRef.current?.click();
  const openLibrary = () => libraryInputRef.current?.click();

  const clearSelection = () => {
    // ✅ ensure clearing truly stops the upload
    cancelUpload();

    setFile(null);
    revokePreview();
    setPreviewUrl(null);

    setProgress(0);
    setError(null);
    setUploadedMeta(null);
    uploadedOnceRef.current = false;
    pendingAutoUploadRef.current = null;

    // ✅ allow re-picking the same file (input change won’t fire otherwise)
    if (inputRef.current) inputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (libraryInputRef.current) libraryInputRef.current.value = "";
  };

  // ✅ upload can accept a fileOverride so we don't rely on React state timing
  const upload = async (fileOverride?: File) => {
    const f = fileOverride ?? file;

    if (!f || uploading) return; // guard against double starts
    setUploading(true);
    setProgress(0);
    setError(null);
    uploadedOnceRef.current = false;

    const uploadUrl =
      N8N_FILE_UPLOAD_URL && /^https?:\/\//i.test(N8N_FILE_UPLOAD_URL)
        ? N8N_FILE_UPLOAD_URL
        : "/api/n8n-upload";

    try {
      if (!uploadUrl) throw new Error("Upload endpoint not configured");

      const form = buildStepUploadFormData({
        file: f,
        sessionId,
        stepId: artifact.stepId,
        artifactId: artifact.id,
        accessCode,
        db,
      });

      const meta = await new Promise<StepUploadMetadata>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.addEventListener("progress", (evt) => {
          if (evt.lengthComputable) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        });

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const json = JSON.parse(xhr.responseText);
                setProgress(100);
                resolve(normalizeStepUploadMetadata(json, f));
              } catch {
                reject(new Error("Invalid upload response"));
              }
            } else {
              // status 0 is typically abort / network / CORS / mixed-content
              reject(new Error(`Upload failed (${xhr.status})`));
            }
          }
        };

        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.ontimeout = () => reject(new Error("Upload timed out"));
        xhr.onabort = () => reject(new Error("Upload canceled"));

        xhr.open("POST", uploadUrl, true);
        xhr.timeout = 5 * 60 * 1000;
        xhr.send(form);
      });

      if (USE_FILE_EVENT) {
        try {
          const feRes = await fetch("/api/file-event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "uploaded",
              sessionId,
              stepId: artifact.stepId,
              artifactId: artifact.id,
              storagePath: meta.storagePath,
              filename: meta.filename,
              mime: meta.mime,
              size: meta.size,
              width: meta.width,
              height: meta.height,
              accessCode,
              db,
            }),
          });
          if (!feRes.ok) {
            setError(
              "Saved file, but logging failed. You can continue; we’ll retry automatically.",
            );
          }
        } catch {
          setError(
            "Saved file, but logging failed. You can continue; we’ll retry automatically.",
          );
        }
      }

      // Keep previewUrl so thumbnail remains visible post-upload.
      setUploadedMeta(meta);

      // Notify parent exactly once so it can enable "Finish".
      if (!uploadedOnceRef.current) {
        uploadedOnceRef.current = true;
        onUploaded?.(meta);
      }
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setUploading(false);
      xhrRef.current = null;
    }
  };

  const handleFile = (f: File) => {
    setError(null);

    if (db === "supabase" && !f.type.startsWith("image/")) {
      setError("Supabase homework uploads accept images only.");
      return;
    }

    // Guard size early
    if (f.size > maxSize) {
      setError(`File is too large. Max ${artifact.maxSizeMB ?? 10} MB.`);
      return;
    }

    // If user is replacing during an active upload, abort the old one
    cancelUpload();

    // Reset uploaded state (we're selecting a fresh file)
    setUploadedMeta(null);
    uploadedOnceRef.current = false;

    // Update preview + file
    revokePreview();
    setFile(f);

    const nextPreview = f.type.startsWith("image/")
      ? URL.createObjectURL(f)
      : null;

    previewUrlRef.current = nextPreview;
    setPreviewUrl(nextPreview);

    // ✅ Auto-upload: schedule after the current event turn
    pendingAutoUploadRef.current = f;
    window.setTimeout(() => {
      if (pendingAutoUploadRef.current !== f) return; // superseded
      pendingAutoUploadRef.current = null;
      void upload(f);
    }, 0);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    // ✅ allow selecting the same file again later
    e.target.value = "";
    if (f) handleFile(f);
  };

  const prevent = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDragEnter = (e: React.DragEvent) => {
    prevent(e);
    setDragActive(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    prevent(e);
    if (!dragActive) setDragActive(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    prevent(e);
    if (e.currentTarget === e.target) setDragActive(false);
  };
  const onDrop = (e: React.DragEvent) => {
    prevent(e);
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const uploadedPreviewSrc =
    uploadedMeta?.publicUrl ||
    previewUrl ||
    (uploadedMeta?.storagePath &&
      /^https?:\/\//i.test(uploadedMeta.storagePath) &&
      uploadedMeta.storagePath) ||
    null;

  return (
    <div
      className="w-full rounded-2xl bg-white shadow p-4 border border-zinc-200 overflow-hidden"
      aria-busy={uploading ? "true" : "false"}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center"
          aria-hidden
        >
          <Paperclip className="w-5 h-5 text-zinc-700" />
        </div>

        <div className="w-full">
          <div className="flex items-center gap-2">
            <h3 className="text-zinc-900 font-semibold">{artifact.label}</h3>
            {artifact.required && (
              <span className="text-red-500" title="Required">
                *
              </span>
            )}
          </div>

          {artifact.hint && (
            <p className="text-zinc-500 text-sm mt-0.5">{artifact.hint}</p>
          )}

          {/* Drop zone (pre-upload) */}
          {!uploadedMeta && (
            <div
              className={`mt-3 rounded-xl border-2 ${
                dragActive ? "border-zinc-900" : "border-dashed border-zinc-300"
              } p-6 text-center cursor-pointer transition-colors`}
              role="button"
              aria-label="Upload file"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                // On mobile photo uploads, keyboard action defaults to library picker
                if (showMobilePhotoButtons) openLibrary();
                else openPicker();
              }}
              onClick={() => {
                // On mobile photo uploads, buttons control camera vs library.
                // Keep desktop + non-photo click-to-browse.
                if (showMobilePhotoButtons) return;
                openPicker();
              }}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <div className="mb-2 flex items-center justify-center">
                {isPhoto ? (
                  <Camera
                    className="w-8 h-8 text-zinc-700"
                    aria-hidden="true"
                  />
                ) : (
                  <FileIcon
                    className="w-8 h-8 text-zinc-700"
                    aria-hidden="true"
                  />
                )}
              </div>

              {showMobilePhotoButtons ? (
                <div className="mt-1 flex flex-col gap-2 items-center">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-xl bg-zinc-900 text-white w-full sm:w-auto"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openCamera();
                    }}
                  >
                    Take photo
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-xl border w-full sm:w-auto"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openLibrary();
                    }}
                  >
                    Choose from photos
                  </button>
                  <p className="text-xs text-zinc-500 mt-1">
                    You can also use Files if available in the chooser.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-zinc-600">
                  <span className="underline">Browse</span> to select a{" "}
                  {artifact.type} or drag &amp; drop here
                </p>
              )}

              <p className="text-xs text-zinc-400 mt-1">
                Accepted: {accept || "any"} • Max {artifact.maxSizeMB ?? 10} MB
              </p>

              {/* Inputs */}
              {showMobilePhotoButtons ? (
                <>
                  {/* Mobile: explicit camera */}
                  <input
                    ref={cameraInputRef}
                    type="file"
                    className="hidden"
                    accept={accept}
                    capture="environment"
                    multiple={!!artifact.multiple}
                    onChange={onInputChange}
                  />
                  {/* Mobile: explicit library/photos */}
                  <input
                    ref={libraryInputRef}
                    type="file"
                    className="hidden"
                    accept={accept}
                    multiple={!!artifact.multiple}
                    onChange={onInputChange}
                  />
                </>
              ) : (
                /* Desktop + non-photo: single picker + drag/drop */
                <input
                  ref={inputRef}
                  type="file"
                  className="hidden"
                  accept={accept}
                  multiple={!!artifact.multiple}
                  onChange={onInputChange}
                />
              )}
            </div>
          )}

          {/* Selected preview (pre-upload) */}
          {!uploadedMeta && file && (
            <div className="mt-3 flex items-center gap-3">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Selected preview"
                  className="w-16 h-16 object-cover rounded-lg border"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg border flex items-center justify-center">
                  <FileIcon
                    className="w-7 h-7 text-zinc-700"
                    aria-hidden="true"
                  />
                </div>
              )}
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-zinc-800">
                  {file.name}
                </div>
                <div className="text-xs text-zinc-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
            </div>
          )}

          {/* Actions (auto-upload: no Upload button) */}
          {!uploadedMeta && (
            <div className="mt-3 flex items-center gap-2">
              {uploading ? (
                <>
                  <div
                    className="px-4 py-2 rounded-xl bg-zinc-900 text-white"
                    aria-live="polite"
                  >
                    Uploading… {progress}%
                  </div>
                  <button
                    onClick={cancelUpload}
                    className="px-3 py-2 rounded-xl border"
                    type="button"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                file && (
                  <button
                    onClick={clearSelection}
                    className="px-3 py-2 rounded-xl border"
                    type="button"
                  >
                    Clear
                  </button>
                )
              )}
            </div>
          )}

          {/* Uploaded state — responsive grid to keep button inside */}
          {uploadedMeta && (
            <div className="mt-3 w-full max-w-full grid grid-cols-[auto,1fr] sm:grid-cols-[auto,1fr,auto] gap-3 items-center">
              {uploadedPreviewSrc ? (
                <img
                  src={uploadedPreviewSrc}
                  alt="Uploaded preview"
                  className="w-16 h-16 object-cover rounded-lg border"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg border flex items-center justify-center">
                  <FileIcon
                    className="w-7 h-7 text-zinc-700"
                    aria-hidden="true"
                  />
                </div>
              )}

              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-zinc-800">
                  {uploadedMeta.filename}
                </div>
                <div className="text-xs text-emerald-600" aria-live="polite">
                  Uploaded ✓
                </div>
              </div>

              <button
                onClick={clearSelection}
                className="px-3 py-2 rounded-xl border justify-self-start sm:justify-self-end col-span-2 sm:col-span-1"
                type="button"
              >
                Replace
              </button>
            </div>
          )}

          {error && (
            <p
              className="mt-2 text-sm text-red-600"
              role="status"
              aria-live="polite"
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepUploadBubble;
